import React, { Fragment, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import profile_img from '../../assets/images/pages/defaultImage.jpg';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi from '../../utils/henceforthApi';
import VerifyOtp from '../../components/modals/VerifyEmailOtp';
import authAction from '../../context/authAction';
import { toast } from 'react-toastify';
import BreadCrumb from '../../components/common/BreadCrumb';
import { Button } from '../../components/ui/button';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  Percent,
  Edit,
  ShieldCheck,
  AlertTriangle,
  Clock,
  XCircle,
  RotateCcw,
  Landmark,
  CreditCard,
  FileText,
  ExternalLink,
  CheckCircle2,
  Check,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

const Profile: React.FC = () => {
  const { language_data, authState, authDispatch, loading, setLoading } = useContext(GlobalContext);
  const [openEmailVerification, setOpenEmailVerification] = useState(false);
  const navigate = useNavigate();

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_profile || 'Profile'}`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const initialiseProfile = async (data: any) => {
    try {
      const apiRes = await henceforthApi.Auth.emailVerifyOtp({ otp: data, language: 'ENGLISH' });
      authAction.initialiseProfile(apiRes.data)(authDispatch);
      setOpenEmailVerification(false);
      toast.success('Email verified successfully!');
    } catch (err) {
      handleError(err, 'active', authDispatch);
    }
  };

  const resendEmailOtp = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const items = {
        email: authState.email,
        language: 'ENGLISH',
      };
      const apiRes = await henceforthApi.Auth.emailVerifySendOtp(items);
      return apiRes.data;
    } catch (error: any) {
      handleError(error, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  const OpenEmailVerification = async () => {
    await resendEmailOtp();
    setOpenEmailVerification(true);
  };

  const handleResubmitProfile = async () => {
    setLoading(true);
    try {
      const apiRes = await henceforthApi.Auth.resubmitProfile();
      if (apiRes?.data) {
        authAction.initialiseProfile(apiRes.data)(authDispatch);
        toast.success('Profile resubmitted successfully! Your account is now under review.');
      }
    } catch (error: any) {
      if (error?.response?.body?.error === 'ALREADY_PENDING') {
        toast.warning('Your profile is already pending verification. Please wait for admin review.');
      } else {
        handleError(error, 'active', authDispatch);
      }
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image: string | null | undefined) => {
    if (!image) return profile_img;
    return image.endsWith('.webp')
      ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${image}`
      : `${henceforthApi.API_FILE_ROOT_MEDIUM}${image}`;
  };

  const getDocumentImageUrl = (image: string | null | undefined) => {
    if (!image) return profile_img;
    return image.endsWith('.webp')
      ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${image}`
      : `${henceforthApi.API_FILE_ROOT_MEDIUM}${image}`;
  };

  const isVerified = authState.is_admin_verified === 'approve' || authState.is_admin_verified === 'approved';
  const isPending = authState.is_admin_verified === 'pending';
  const isRejected = authState.is_admin_verified === 'reject' || authState.is_admin_verified === 'rejected';

  return (
    <Fragment>
      {/* Standard Breadcrumb Bar */}
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-5">
        {/* 1. Header Card */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_my || 'My'}{' '}
                {language_data?.[authState.lang]?.common_profile || 'Profile'}
              </h1>
              {isVerified && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  Verified Seller
                </span>
              )}
              {isPending && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 shadow-2xs">
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                  Verification Pending
                </span>
              )}
              {isRejected && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200 shadow-2xs">
                  <XCircle className="w-3.5 h-3.5 text-rose-600" />
                  Action Required
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
              Manage your seller account details, business identity, bank payout preferences, and compliance documents.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <Button variant="default" size="sm" asChild>
              <Link to="/profile/edit" className="inline-flex items-center gap-2">
                <Edit className="w-4 h-4" />
                <span>{language_data?.[authState.lang]?.common_edit || 'Edit'} Profile</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* 2. Admin Verification Status Banners */}
        {isRejected && (
          <div className="p-4 rounded-2xl bg-rose-50/80 border border-rose-200 shadow-xs">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-100 border border-rose-200 flex items-center justify-center shrink-0">
                <XCircle className="w-6 h-6 text-rose-600" />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-base font-black text-rose-900 m-0">
                    Account Verification Rejected
                  </h3>
                  <p className="text-xs sm:text-sm text-rose-700 mt-1 mb-0 font-medium">
                    Your seller account application was not approved by the platform administration. Please review the reason below and update your documents or business information.
                  </p>
                </div>

                {(authState as any)?.rejection_reason && (
                  <div className="p-3.5 rounded-xl bg-white border border-rose-200/80 shadow-2xs">
                    <span className="text-xs font-bold text-rose-800 uppercase tracking-wider block mb-1">
                      Reason for Rejection:
                    </span>
                    <p className="text-xs sm:text-sm text-slate-700 m-0 font-medium">
                      {(authState as any)?.rejection_reason}
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-2.5 pt-1 flex-wrap">
                  <Button variant="destructive" size="sm" onClick={() => navigate('/profile/edit')}>
                    <Edit className="w-4 h-4 me-1.5" />
                    Edit & Resubmit Profile
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResubmitProfile}
                    disabled={loading}
                    className="border-rose-200 text-rose-700 hover:bg-rose-100/50"
                  >
                    <RotateCcw className="w-4 h-4 me-1.5" />
                    Resubmit Without Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isPending && (
          <div className="p-4 rounded-2xl bg-amber-50/80 border border-amber-200 shadow-xs flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-black text-amber-900 m-0">
                Verification in Progress
              </h3>
              <p className="text-xs sm:text-sm text-amber-700 mt-1 mb-0 font-medium">
                Your profile information and compliance documents are currently under review by our administrators. You will be notified as soon as review is completed.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/profile/edit')}
              className="border-amber-200 text-amber-800 hover:bg-amber-100/50 shrink-0"
            >
              <Edit className="w-4 h-4 me-1.5" />
              Update Information
            </Button>
          </div>
        )}

        {/* 3. Main Profile Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Card 1: Seller Details */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 shadow-xs flex flex-col justify-between">
            <div className="p-6 space-y-6">
              {/* Profile Header: Avatar + Name + Commission */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="relative shrink-0">
                    <img
                      src={getImageUrl(authState?.image)}
                      alt={authState?.name || 'Profile'}
                      className="w-20 h-20 rounded-2xl object-cover border border-slate-200 shadow-2xs bg-slate-50"
                    />
                  </div>

                  <div>
                    <h2 className="text-xl font-black text-slate-900 m-0">
                      {authState.name || 'Store Owner'}
                    </h2>
                    {authState.company && (
                      <p className="text-xs sm:text-sm text-slate-500 font-semibold flex items-center gap-1.5 mt-1 mb-0">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        {authState.company}
                      </p>
                    )}
                  </div>
                </div>

                {(authState as any)?.admin_commission && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-50 text-purple-700 border border-purple-200 font-bold text-xs self-start sm:self-center">
                    <Percent className="w-3.5 h-3.5" />
                    Commission: {(authState as any).admin_commission}%
                  </span>
                )}
              </div>

              {/* Info Fields Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Email */}
                <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/60 space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                    Email Address
                  </span>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="text-xs sm:text-sm font-semibold text-slate-800 truncate">
                        {authState.email || '—'}
                      </span>
                    </div>
                    {authState.email_verified ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 shrink-0">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        Verified
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={OpenEmailVerification}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 px-2.5 py-0.5 rounded-full border border-amber-200 transition-colors shrink-0 cursor-pointer"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                        Verify
                      </button>
                    )}
                  </div>
                </div>

                {/* Phone */}
                <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/60 space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                    Phone Number
                  </span>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold text-slate-800">
                      {authState.phone_number
                        ? `+${authState.country_code} ${authState.phone_number}`
                        : '—'}
                    </span>
                  </div>
                </div>

                {/* Store / Full Address */}
                <div className="sm:col-span-2 p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/60 space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                    Business Address
                  </span>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm font-semibold text-slate-800 leading-snug">
                      {authState.full_address || 'No business address provided yet.'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Payout & Bank Details Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 flex flex-col justify-between">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-slate-900 m-0">Bank Account</h3>
                    <span className="text-xs text-slate-400 font-medium">Direct deposit payouts</span>
                  </div>
                </div>

                {authState?.is_bank_added ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                    Setup Needed
                  </span>
                )}
              </div>

              {/* Bank Virtual Card Preview */}
              <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white shadow-md relative overflow-hidden space-y-4">
                <div className="flex items-center justify-between">
                  <CreditCard className="w-7 h-7 text-emerald-400" />
                  <span className="text-[10px] uppercase tracking-widest font-black text-slate-400">
                    Seller Payout
                  </span>
                </div>

                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                    Account Status
                  </span>
                  <span className="text-sm font-black text-white tracking-wide">
                    {authState?.is_bank_added ? 'Verified Settlement Account' : 'No Account Connected'}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-700/60 text-xs font-medium text-slate-300">
                  <span>{authState.name || 'Store Holder'}</span>
                  <span>{authState?.is_bank_added ? '•••• Encrypted' : 'Pending'}</span>
                </div>
              </div>

              <p className="text-xs text-slate-500 font-medium leading-relaxed m-0">
                {authState?.is_bank_added
                  ? 'Your bank account credentials are encrypted and active for weekly automated sales payouts.'
                  : 'Please connect your verified bank routing and account number to enable automatic earnings deposits.'}
              </p>
            </div>

            <div className="pt-4 mt-4 border-t border-slate-100">
              <Button
                variant={authState?.is_bank_added ? 'outline' : 'default'}
                className="w-full justify-center"
                onClick={() => navigate('/profile/bank-details')}
              >
                <Landmark className="w-4 h-4 me-2" />
                {authState?.is_bank_added ? 'View / Manage Bank Details' : 'Add Bank Account'}
              </Button>
            </div>
          </div>
        </div>

        {/* 4. Documents & Compliance Card */}
        {(authState as any)?.documents && Array.isArray((authState as any).documents) && (authState as any).documents.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#1AA7DE]" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900 m-0">
                    Identity Verification Documents
                  </h3>
                  <span className="text-xs text-slate-400 font-medium">
                    Government-issued identity cards submitted for seller compliance
                  </span>
                </div>
              </div>

              <Button variant="outline" size="sm" asChild>
                <Link to="/profile/edit" className="inline-flex items-center gap-1.5">
                  <Edit className="w-3.5 h-3.5" />
                  Update Documents
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(authState as any).documents.map((doc: any) => (
                <div key={doc._id} className="p-4 rounded-xl border border-slate-200/80 bg-slate-50/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      {doc.type ? doc.type.replace(/_/g, ' ') : 'Document'}
                    </span>
                    <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                      Uploaded
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2.5">
                    {doc.images?.front && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block text-center">
                          Front Side
                        </span>
                        <div
                          className="h-28 rounded-lg border border-slate-200 overflow-hidden bg-white shadow-2xs group relative cursor-pointer"
                          onClick={() => window.open(getDocumentImageUrl(doc.images.front), '_blank')}
                          title="Click to view full image"
                        >
                          <img
                            src={getDocumentImageUrl(doc.images.front)}
                            alt={`${doc.type} front`}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-200"
                          />
                          <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                            <ExternalLink className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    )}

                    {doc.images?.back && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block text-center">
                          Back Side
                        </span>
                        <div
                          className="h-28 rounded-lg border border-slate-200 overflow-hidden bg-white shadow-2xs group relative cursor-pointer"
                          onClick={() => window.open(getDocumentImageUrl(doc.images.back), '_blank')}
                          title="Click to view full image"
                        >
                          <img
                            src={getDocumentImageUrl(doc.images.back)}
                            alt={`${doc.type} back`}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-200"
                          />
                          <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                            <ExternalLink className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Email Verification OTP Modal */}
      {openEmailVerification && (
        <VerifyOtp
          verifyCallback={initialiseProfile}
          resendOtpCallback={resendEmailOtp}
          onCloseModal={setOpenEmailVerification}
        />
      )}
    </Fragment>
  );
};

export default Profile;